import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  FileText, Plus, Trash2, Loader2, Info, Save, Send, Sparkles,
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { applyHiddenTaxToItems, HIDDEN_TAX_RATE } from '../../lib/hiddenTax';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pydantic 422 renvoie detail = [{type, loc, msg, ...}]. React crash si on rend l'objet.
// Helper safe pour extraire un texte lisible peu importe le format renvoyé par le backend.
const extractErrorMessage = (err, fallback = 'Erreur inattendue') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map(d => (typeof d === 'string' ? d : (d?.msg || d?.message || JSON.stringify(d))))
      .join(' · ');
  }
  if (typeof detail === 'object') return detail.msg || detail.message || JSON.stringify(detail);
  return String(detail);
};

const QUOTE_TYPES = [
  { value: 'ponctuel', label: 'Transport ponctuel' },
  { value: 'location_mensuelle', label: 'Location mensuelle' },
  { value: 'mise_disposition', label: 'Mise à disposition de camion' },
  { value: 'recurrent', label: 'Transport récurrent' },
  { value: 'assurance', label: 'Transport avec assurance marchandise' },
  { value: 'intraland', label: 'Transport Intraland' },
  { value: 'hinterland', label: 'Transport Hinterland' },
  { value: 'interland', label: 'Transport Interland / transfrontalier' },
];

const CATEGORIES = [
  { value: 'Intraland', label: 'Intraland (CI seulement)' },
  { value: 'Hinterland', label: 'Hinterland (corridors intérieurs)' },
  { value: 'Interland', label: 'Interland (transfrontalier)' },
  { value: 'Mixte', label: 'Mixte' },
];

const BILLING_UNITS = [
  { value: 'operation', label: 'Opération' },
  { value: 'trip', label: 'Trajet' },
  { value: 'truck_day', label: 'Camion / jour' },
  { value: 'truck_month', label: 'Camion / mois' },
  { value: 'ton', label: 'Tonne' },
  { value: 'forfait', label: 'Forfait' },
];

const FEE_FIELDS = [
  { key: 'fuelIncluded', label: 'Carburant' },
  { key: 'tollsIncluded', label: 'Péage' },
  { key: 'weighFeesIncluded', label: 'Pesage' },
  { key: 'parkingIncluded', label: 'Stationnement' },
  { key: 'handlingIncluded', label: 'Manutention' },
  { key: 'loadingIncluded', label: 'Chargement' },
  { key: 'unloadingIncluded', label: 'Déchargement' },
  { key: 'basicInsuranceIncluded', label: 'Assurance de base' },
  { key: 'extraInsuranceIncluded', label: 'Assurance complémentaire' },
  { key: 'immobilizationFeesIncluded', label: 'Frais d\'immobilisation' },
];

/**
 * Modal de création/édition d'un devis contrat (PM Spec 29/04 + 30/04/2026).
 * Pré-remplit auto les items selon billingMode du contrat.
 * Stricte hérédité depuis le contrat parent (Q2=a) — édition libre par dessus.
 * Bouton "🪄 Générer les conditions auto" — règles rule-based (Q4=a).
 */
const CreateContractQuoteModal = ({ open, onOpenChange, contract, existingQuote = null, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [items, setItems] = useState([]);
  const [applyTVA, setApplyTVA] = useState(false);
  const [tauxTVA, setTauxTVA] = useState(18);
  const [applyHiddenTax, setApplyHiddenTax] = useState(false);
  const [billingPolicy, setBillingPolicy] = useState(null);
  const [validityDays, setValidityDays] = useState(30);
  const [serviceTerms, setServiceTerms] = useState({
    nbVehicles: 0, vehicleTypeLabel: '',
    workingDaysLabel: 'Lundi au samedi',
    initialDurationLabel: '1 mois renouvelable',
    operationType: '', fuelPolicyLabel: 'À votre charge',
    weighFeesPolicyLabel: 'À votre charge', additionalConditions: [],
    quoteType: 'location_mensuelle',
    operationCategory: 'Intraland',
    zoneOperation: "Côte d'Ivoire",
    loadingPlace: '', deliveryPlace: '',
    plannedRotations: null, planningDeadlineHours: 24,
    withDriver: true, isDedicatedFleet: false, billingUnit: 'truck_month',
    fuelIncluded: null, tollsIncluded: null, weighFeesIncluded: null,
    parkingIncluded: null, handlingIncluded: null, loadingIncluded: null,
    unloadingIncluded: null, basicInsuranceIncluded: null,
    extraInsuranceIncluded: null, immobilizationFeesIncluded: null,
    cancellationTerms: '', specialNotes: '',
  });
  const [paymentTerms, setPaymentTerms] = useState({
    advancePercent: 30,
    advanceLabel: 'Au démarrage de chaque mois',
    paymentDeadlineDays: 10,
    paymentDeadlineLabel: 'après dépôt facture FNE',
    paymentMethods: ['virement_bancaire'],
    latePenaltyText: 'Taux légal applicable',
  });
  const [billingMode, setBillingMode] = useState('');

  const isEditMode = !!existingQuote;

  // Init / Pré-remplissage
  useEffect(() => {
    if (!open || !contract) return;
    // Politique de facturation globale (Paramètres → FNE) : régime sans TVA => TVA interdite
    axios.get(`${API}/fne/billing-policy`).then(({ data }) => {
      setBillingPolicy(data);
      if (data?.vatBlocked) {
        setApplyTVA(false);
        setTauxTVA(0);
      }
      if (!existingQuote) {
        setApplyHiddenTax(!!data?.hiddenTaxEnabled);
      }
    }).catch(() => {});

    if (existingQuote) {
      // Mode édition : recharger les données du devis (toujours éditer sur le prix de BASE)
      setItems((existingQuote.items || []).map(i => {
        const baseUnit = i.prix_unitaire_base_ht ?? i.prix_unitaire_ht ?? 0;
        const qte = parseFloat(i.quantite) || 0;
        return { ...i, prix_unitaire_ht: baseUnit, montant_total_ht: qte * baseUnit, prix_unitaire_base_ht: undefined };
      }));
      setApplyTVA(!!existingQuote.applyTVA);
      setTauxTVA(existingQuote.tauxTVA || 18);
      setApplyHiddenTax(!!existingQuote.hiddenTax?.applied);
      setValidityDays(existingQuote.validityDays || 30);
      if (existingQuote.serviceTerms) setServiceTerms(existingQuote.serviceTerms);
      if (existingQuote.paymentTerms) setPaymentTerms(existingQuote.paymentTerms);
      setBillingMode(contract.billingRules?.billingMode || '');
      return;
    }
    // Mode création : fetch defaults from contract
    setLoading(true);
    axios.get(`${API}/contracts/${contract.id}/quotes/_defaults/from-contract`)
      .then(r => {
        const d = r.data || {};
        setBillingMode(d.billingMode || '');
        setItems((d.items || []).map(it => ({
          designation: it.designation || '',
          poids: it.poids || '',
          unite_payante: it.unite_payante || '',
          quantite: it.quantite || 1,
          prix_unitaire_ht: it.prix_unitaire_ht || 0,
          montant_total_ht: it.montant_total_ht || 0,
          commentaire: it.commentaire || '',
          _prefilled: true,
        })));
        // Pattern fonctionnel pour éviter la stale closure ET garder serviceTerms hors deps
        setServiceTerms(prev => ({ ...prev, ...(d.serviceTerms || {}) }));
      })
      .catch(() => {
        toast({ title: 'Erreur', description: 'Impossible de charger les données contrat', variant: 'destructive' });
      })
      .finally(() => setLoading(false));
    // ⚠️ NE PAS ajouter serviceTerms/paymentTerms aux deps : leur référence change au setState,
    // ce qui créerait une boucle infinie de re-fetch. Voulu : seuls open/contract/quote triggers.
  }, [open, contract?.id, existingQuote?.id]);

  // Calcul auto des totaux — lignes effectives avec taxe cachée 2% incorporée si activée
  const { items: effItems, meta: hiddenTaxMeta } = applyHiddenTaxToItems(items, applyHiddenTax);
  const subtotalHT = effItems.reduce((s, it) => s + (parseFloat(it.montant_total_ht) || 0), 0);
  const montantTVA = applyTVA ? Math.round(subtotalHT * tauxTVA / 100) : 0;
  const totalTTC = subtotalHT + montantTVA;

  const updateItem = (index, key, value) => {
    setItems(prev => {
      const next = [...prev];
      const item = { ...next[index], [key]: value };
      if (key === 'quantite' || key === 'prix_unitaire_ht') {
        const q = parseFloat(item.quantite) || 0;
        const pu = parseFloat(item.prix_unitaire_ht) || 0;
        item.montant_total_ht = q * pu;
      }
      next[index] = item;
      return next;
    });
  };

  const addItem = () => setItems(prev => [...prev, {
    designation: '', poids: '', unite_payante: 'Forfait', quantite: 1,
    prix_unitaire_ht: 0, montant_total_ht: 0, commentaire: '',
  }]);

  const removeItem = (index) => setItems(prev => prev.filter((_, i) => i !== index));

  // Pydantic v2 rejette les strings vides sur les champs typés int/float.
  // On normalise explicitement les nombres avant d'envoyer.
  const sanitizePayload = (payload) => {
    const cleanItems = (payload.items || []).map(it => ({
      ...it,
      poids: (it.poids === '' || it.poids === undefined || it.poids === null) ? null : (Number(it.poids) || null),
      quantite: Number(it.quantite) || 0,
      prix_unitaire_ht: Number(it.prix_unitaire_ht) || 0,
      montant_total_ht: Number(it.montant_total_ht) || 0,
    }));
    const cleanPayment = {
      ...(payload.paymentTerms || {}),
      advancePercent: Number(payload.paymentTerms?.advancePercent) || 0,
      paymentDeadlineDays: Number(payload.paymentTerms?.paymentDeadlineDays) || 0,
    };
    const st = payload.serviceTerms || {};
    const toNumOrNull = (v) => (v === '' || v === undefined || v === null ? null : Number(v) || null);
    const cleanService = {
      ...st,
      nbVehicles: Number(st.nbVehicles) || 0,
      plannedRotations: toNumOrNull(st.plannedRotations),
      planningDeadlineHours: toNumOrNull(st.planningDeadlineHours),
    };
    return {
      ...payload,
      items: cleanItems,
      tauxTVA: Number(payload.tauxTVA) || 18,
      validityDays: Number(payload.validityDays) || 30,
      paymentTerms: cleanPayment,
      serviceTerms: cleanService,
    };
  };

  // PM Spec 30/04/2026 (Q2=a) — Régénération des conditions SANS créer de draft en base.
  // On appelle l'endpoint preview qui calcule les bullets sans persister.
  const handleRegenerate = async () => {
    if (items.length === 0) {
      toast({ title: 'Validation', description: 'Ajoutez d\'abord les lignes du devis', variant: 'destructive' });
      return;
    }
    setRegenerating(true);
    try {
      const payload = sanitizePayload({
        items: effItems.map(({ _prefilled, ...rest }) => rest),
        applyTVA, tauxTVA, validityDays,
        serviceTerms, paymentTerms,
        hiddenTax: hiddenTaxMeta,
      });
      const r = await axios.post(`${API}/contracts/${contract.id}/quotes/_preview-conditions`, payload);
      const result = r.data || {};
      setServiceTerms(prev => ({
        ...prev,
        additionalConditions: result.additionalConditions || [],
        cancellationTerms: result.cancellationTerms || prev.cancellationTerms,
      }));
      toast({
        title: 'Conditions générées',
        description: `${(result.additionalConditions || []).length} clauses ajoutées (modifiables ci-dessous)`,
      });
    } catch (err) {
      toast({
        title: 'Erreur',
        description: extractErrorMessage(err, 'Impossible de générer les conditions'),
        variant: 'destructive',
      });
    } finally {
      setRegenerating(false);
    }
  };

  const handleSave = async (alsoSend = false) => {
    if (items.length === 0) {
      toast({ title: 'Validation', description: 'Ajoutez au moins une ligne au devis', variant: 'destructive' });
      return;
    }
    if (items.some(it => !it.designation?.trim())) {
      toast({ title: 'Validation', description: 'Chaque ligne doit avoir une désignation', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = sanitizePayload({
        items: effItems.map(({ _prefilled, ...rest }) => rest),
        applyTVA, tauxTVA, validityDays,
        serviceTerms, paymentTerms,
        hiddenTax: hiddenTaxMeta,
      });
      let quoteId;
      if (existingQuote) {
        await axios.patch(`${API}/contracts/${contract.id}/quotes/${existingQuote.id}`, payload);
        quoteId = existingQuote.id;
      } else {
        const r = await axios.post(`${API}/contracts/${contract.id}/quotes`, payload);
        quoteId = r.data.id;
      }
      if (alsoSend) {
        await axios.post(`${API}/contracts/${contract.id}/quotes/${quoteId}/send`);
        toast({ title: 'Succès', description: 'Devis envoyé au client' });
      } else {
        toast({ title: 'Succès', description: existingQuote ? 'Devis mis à jour' : 'Devis créé en brouillon' });
      }
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: extractErrorMessage(err, 'Impossible d\'enregistrer le devis'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="create-contract-quote-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" />
            {existingQuote ? `Modifier le devis ${existingQuote.quoteNumber} (V${existingQuote.version})` : 'Nouveau devis contrat'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        ) : (
          <div className="space-y-5 py-2">

            {/* Bandeau info pré-remplissage */}
            {!existingQuote && billingMode && items.some(i => i._prefilled) && (
              <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-800">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Devis pré-rempli depuis le contrat (mode <strong>{billingMode === 'fleet_location' ? 'location de flotte' : billingMode === 'monthly_fixed' ? 'forfait mensuel' : 'à l\'unité'}</strong>). Les champs restent modifiables.
                </span>
              </div>
            )}
            {!existingQuote && billingMode === 'per_unit' && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Mode "à l'unité" — saisissez manuellement les lignes selon le volume estimé.</span>
              </div>
            )}

            {/* Items */}
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-2">Lignes de prestation</h3>
              <div className="space-y-2">
                {items.map((item, i) => (
                  <Card key={i} className="border" data-testid={`quote-item-${i}`}>
                    <CardContent className="p-3 space-y-2">
                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-4">
                          <Label className="text-xs">Désignation</Label>
                          <Input value={item.designation} onChange={e => updateItem(i, 'designation', e.target.value)}
                                 className="h-8 text-sm" data-testid={`item-designation-${i}`} />
                        </div>
                        <div className="col-span-1">
                          <Label className="text-xs">Poids (T)</Label>
                          <Input type="number" min="0" step="any" value={item.poids ?? ''}
                                 onChange={e => updateItem(i, 'poids', e.target.value)}
                                 className="h-8 text-sm" data-testid={`item-poids-${i}`}
                                 placeholder="—" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Unité</Label>
                          <Input value={item.unite_payante} onChange={e => updateItem(i, 'unite_payante', e.target.value)}
                                 className="h-8 text-sm" />
                        </div>
                        <div className="col-span-1">
                          <Label className="text-xs">Qté</Label>
                          <Input type="number" min="0" step="any" value={item.quantite}
                                 onChange={e => updateItem(i, 'quantite', e.target.value)} className="h-8 text-sm" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">PU HT (F)</Label>
                          <Input type="number" min="0" value={item.prix_unitaire_ht}
                                 onChange={e => updateItem(i, 'prix_unitaire_ht', e.target.value)} className="h-8 text-sm" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Total HT (F)</Label>
                          <div className="h-8 px-3 flex items-center bg-gray-50 rounded text-sm font-semibold">
                            {(parseFloat(item.montant_total_ht) || 0).toLocaleString('fr-FR')}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <Input placeholder="Commentaire (optionnel)" value={item.commentaire || ''}
                               onChange={e => updateItem(i, 'commentaire', e.target.value)} className="h-7 text-xs flex-1" />
                        {items.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeItem(i)}
                                  className="h-7 w-7 p-0 text-red-500 hover:text-red-700">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addItem}
                      className="mt-2 border-dashed text-emerald-600 border-emerald-300 hover:bg-emerald-50">
                <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une ligne
              </Button>
            </div>

            {/* Totaux */}
            <div className="flex justify-end">
              <div className="w-72 space-y-1 text-sm bg-gray-50 rounded-md p-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Sous-total HT</span>
                  <span className="font-semibold">{subtotalHT.toLocaleString('fr-FR')} F</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch checked={applyTVA} onCheckedChange={setApplyTVA}
                            disabled={!!billingPolicy?.vatBlocked} data-testid="apply-tva-switch" />
                    <span className={billingPolicy?.vatBlocked ? 'text-gray-400' : 'text-gray-600'}>
                      TVA {billingPolicy?.vatBlocked && '(désactivée)'}
                    </span>
                  </div>
                  {applyTVA && (
                    <div className="flex items-center gap-1">
                      <Input type="number" value={tauxTVA} onChange={e => setTauxTVA(e.target.value)}
                             className="h-6 w-14 text-xs text-right" />
                      <span className="text-xs text-gray-500">%</span>
                    </div>
                  )}
                </div>
                {billingPolicy?.vatBlocked && (
                  <p className="text-[11px] leading-snug text-gray-500" data-testid="contract-quote-vat-blocked">
                    {billingPolicy.message}
                  </p>
                )}
                {applyTVA && (
                  <div className="flex justify-between text-gray-700">
                    <span>Montant TVA</span>
                    <span>{montantTVA.toLocaleString('fr-FR')} F</span>
                  </div>
                )}
                <div className="flex items-center justify-between border-t border-violet-200 pt-1.5 mt-1.5" data-testid="hidden-tax-block">
                  <div className="flex items-center gap-2">
                    <Switch checked={applyHiddenTax} onCheckedChange={setApplyHiddenTax} data-testid="apply-hidden-tax-switch" />
                    <span className="text-violet-700">Taxe cachée {HIDDEN_TAX_RATE}%</span>
                  </div>
                  {applyHiddenTax && (
                    <span className="text-xs text-violet-700" data-testid="hidden-tax-amount">
                      +{(hiddenTaxMeta.taxTotal || 0).toLocaleString('fr-FR')} F
                    </span>
                  )}
                </div>
                {applyHiddenTax && (
                  <p className="text-[11px] text-violet-600 leading-tight">
                    Incorporée dans les prix unitaires (arrondis au 100 F). Invisible pour le client.
                  </p>
                )}
                <div className="flex justify-between font-bold text-emerald-700 border-t border-emerald-200 pt-1.5 mt-1.5">
                  <span>TOTAL TTC</span>
                  <span>{totalTTC.toLocaleString('fr-FR')} F</span>
                </div>
              </div>
            </div>

            {/* Validité */}
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Validité (jours)</Label>
              <Input type="number" min="1" value={validityDays}
                     onChange={e => setValidityDays(e.target.value)} className="h-8 w-24 text-sm" />
              <Badge variant="outline" className="text-xs">Devis valable {validityDays} j</Badge>
            </div>

            {/* === Type de devis & catégorie === */}
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-2">Cadrage commercial</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-xs">Type de devis <span className="text-red-500">*</span></Label>
                  <Select value={serviceTerms.quoteType || ''}
                          onValueChange={v => setServiceTerms(prev => ({ ...prev, quoteType: v }))}>
                    <SelectTrigger className="h-8 text-sm" data-testid="quote-type-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                    <SelectContent>
                      {QUOTE_TYPES.map(qt => <SelectItem key={qt.value} value={qt.value}>{qt.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Catégorie d'opération</Label>
                  <Select value={serviceTerms.operationCategory || ''}
                          onValueChange={v => setServiceTerms(prev => ({ ...prev, operationCategory: v }))}>
                    <SelectTrigger className="h-8 text-sm" data-testid="operation-category-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Lieu de chargement</Label>
                  <Input value={serviceTerms.loadingPlace || ''}
                         onChange={e => setServiceTerms(prev => ({ ...prev, loadingPlace: e.target.value }))}
                         className="h-8 text-sm" placeholder="Abidjan, Port" />
                </div>
                <div>
                  <Label className="text-xs">Lieu de livraison</Label>
                  <Input value={serviceTerms.deliveryPlace || ''}
                         onChange={e => setServiceTerms(prev => ({ ...prev, deliveryPlace: e.target.value }))}
                         className="h-8 text-sm" placeholder="Bouake" />
                </div>
                <div>
                  <Label className="text-xs">Unité de facturation</Label>
                  <Select value={serviceTerms.billingUnit || ''}
                          onValueChange={v => setServiceTerms(prev => ({ ...prev, billingUnit: v }))}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                    <SelectContent>
                      {BILLING_UNITS.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Délai planning client (h)</Label>
                  <Input type="number" min="0" value={serviceTerms.planningDeadlineHours || ''}
                         onChange={e => setServiceTerms(prev => ({ ...prev, planningDeadlineHours: parseInt(e.target.value) || null }))}
                         className="h-8 text-sm" placeholder="24" />
                </div>
                <div>
                  <Label className="text-xs">Rotations prévues</Label>
                  <Input type="number" min="0" value={serviceTerms.plannedRotations || ''}
                         onChange={e => setServiceTerms(prev => ({ ...prev, plannedRotations: parseInt(e.target.value) || null }))}
                         className="h-8 text-sm" placeholder="Optionnel" />
                </div>
                <div className="flex items-end gap-4 col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={!!serviceTerms.withDriver}
                              onCheckedChange={v => setServiceTerms(prev => ({ ...prev, withDriver: !!v }))} />
                    <span className="text-xs">Avec chauffeur</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={!!serviceTerms.isDedicatedFleet}
                              onCheckedChange={v => setServiceTerms(prev => ({ ...prev, isDedicatedFleet: !!v }))} />
                    <span className="text-xs">Flotte dédiée</span>
                  </label>
                </div>
              </div>
            </div>

            {/* === Frais inclus / exclus (tri-state) === */}
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-2">Frais inclus / exclus</h3>
              <p className="text-xs text-gray-500 mb-2">Coche = inclus, décoche après coche = exclus, vide = non spécifié.</p>
              <div className="grid grid-cols-3 gap-2 text-sm">
                {FEE_FIELDS.map(f => {
                  const val = serviceTerms[f.key];
                  const cycle = val === true ? false : val === false ? null : true;
                  const labelCls = val === true ? 'text-emerald-700 font-medium'
                                  : val === false ? 'text-red-600 line-through'
                                  : 'text-gray-500';
                  return (
                    <button key={f.key} type="button"
                            data-testid={`fee-${f.key}`}
                            className={`px-2 py-1.5 border rounded text-left text-xs ${val === true ? 'border-emerald-300 bg-emerald-50' : val === false ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}
                            onClick={() => setServiceTerms(prev => ({ ...prev, [f.key]: cycle }))}>
                      <span className={labelCls}>{f.label}</span>
                      <span className="ml-1 text-[10px] text-gray-400">
                        {val === true ? '✓ inclus' : val === false ? '✗ exclus' : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* === Bouton Générer auto + zone notes & annulation === */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-sm text-gray-700">Conditions générales (auto-générables)</h3>
                <Button type="button" variant="outline" size="sm"
                        onClick={handleRegenerate} disabled={regenerating}
                        data-testid="regenerate-conditions-btn"
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                  {regenerating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                  Générer les conditions auto
                </Button>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Génère intro, périmètre, frais, paiement et clauses d'annulation depuis les champs ci-dessus.
                Tout reste éditable en live.
              </p>
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Notes particulières (optionnel)</Label>
                  <Textarea value={serviceTerms.specialNotes || ''}
                            onChange={e => setServiceTerms(prev => ({ ...prev, specialNotes: e.target.value }))}
                            rows={2} className="text-sm"
                            placeholder="Ex : Reconduction tacite par périodes d'1 mois sauf préavis 15 jours" />
                </div>
                {(serviceTerms.additionalConditions || []).length > 0 && (
                  <div>
                    <Label className="text-xs">Clauses générées (modifiables)</Label>
                    <div className="space-y-1 mt-1" data-testid="additional-conditions-list">
                      {(serviceTerms.additionalConditions || []).map((c, idx) => (
                        <div key={idx} className="flex gap-2 items-start">
                          <Textarea value={c} rows={1} className="text-xs flex-1"
                                    onChange={e => {
                                      const next = [...serviceTerms.additionalConditions];
                                      next[idx] = e.target.value;
                                      setServiceTerms(prev => ({ ...prev, additionalConditions: next }));
                                    }} />
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500"
                                  onClick={() => setServiceTerms(prev => ({
                                    ...prev,
                                    additionalConditions: prev.additionalConditions.filter((_, i) => i !== idx),
                                  }))}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-xs">Conditions d'annulation</Label>
                  <Textarea value={serviceTerms.cancellationTerms || ''}
                            onChange={e => setServiceTerms(prev => ({ ...prev, cancellationTerms: e.target.value }))}
                            rows={2} className="text-sm"
                            placeholder="Cliquez sur 'Générer auto' pour pré-remplir" />
                </div>
              </div>
            </div>

            {/* Conditions de service */}
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-2">Conditions de service</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-xs">Nombre de véhicules</Label>
                  <Input type="number" value={serviceTerms.nbVehicles}
                         onChange={e => setServiceTerms({ ...serviceTerms, nbVehicles: parseInt(e.target.value) || 0 })}
                         className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Type de véhicule</Label>
                  <Input value={serviceTerms.vehicleTypeLabel}
                         onChange={e => setServiceTerms({ ...serviceTerms, vehicleTypeLabel: e.target.value })}
                         className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Jours travaillés</Label>
                  <Input value={serviceTerms.workingDaysLabel}
                         onChange={e => setServiceTerms({ ...serviceTerms, workingDaysLabel: e.target.value })}
                         className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Durée initiale</Label>
                  <Input value={serviceTerms.initialDurationLabel}
                         onChange={e => setServiceTerms({ ...serviceTerms, initialDurationLabel: e.target.value })}
                         className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Type d'opération</Label>
                  <Input value={serviceTerms.operationType}
                         onChange={e => setServiceTerms({ ...serviceTerms, operationType: e.target.value })}
                         className="h-8 text-sm" placeholder="Intraland / Côte d'Ivoire" />
                </div>
                <div>
                  <Label className="text-xs">Carburant</Label>
                  <Input value={serviceTerms.fuelPolicyLabel}
                         onChange={e => setServiceTerms({ ...serviceTerms, fuelPolicyLabel: e.target.value })}
                         className="h-8 text-sm" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Frais de pesage et de péage</Label>
                  <Input value={serviceTerms.weighFeesPolicyLabel}
                         onChange={e => setServiceTerms({ ...serviceTerms, weighFeesPolicyLabel: e.target.value })}
                         className="h-8 text-sm" />
                </div>
              </div>
            </div>

            {/* Conditions de paiement */}
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-2">Conditions de paiement</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-xs">Avance (%)</Label>
                  <Input type="number" min="0" max="100" value={paymentTerms.advancePercent || 0}
                         onChange={e => setPaymentTerms({ ...paymentTerms, advancePercent: parseFloat(e.target.value) || 0 })}
                         className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Libellé de l'avance</Label>
                  <Input value={paymentTerms.advanceLabel || ''}
                         onChange={e => setPaymentTerms({ ...paymentTerms, advanceLabel: e.target.value })}
                         className="h-8 text-sm" placeholder="Au démarrage de chaque mois" />
                </div>
                <div>
                  <Label className="text-xs">Délai paiement (jours)</Label>
                  <Input type="number" min="0" value={paymentTerms.paymentDeadlineDays}
                         onChange={e => setPaymentTerms({ ...paymentTerms, paymentDeadlineDays: parseInt(e.target.value) || 0 })}
                         className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Libellé du délai</Label>
                  <Input value={paymentTerms.paymentDeadlineLabel || ''}
                         onChange={e => setPaymentTerms({ ...paymentTerms, paymentDeadlineLabel: e.target.value })}
                         className="h-8 text-sm" placeholder="après dépôt facture FNE" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Pénalités de retard</Label>
                  <Textarea value={paymentTerms.latePenaltyText || ''}
                            onChange={e => setPaymentTerms({ ...paymentTerms, latePenaltyText: e.target.value })}
                            className="text-sm" rows={2} />
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} data-testid="save-draft-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer en brouillon
          </Button>
          {!existingQuote && (
            <Button className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleSave(true)} disabled={saving} data-testid="save-and-send-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Enregistrer et envoyer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateContractQuoteModal;
